<script setup>
import { reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

// Safely converts a value to a number, preserving 0 as a valid entry.
// Returns null for empty strings, null, and undefined — but NOT for 0.
const toNumericOrNull = value => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const state = reactive({
  // Automation — Fixed Initialization
  fixedInitialization: true,
  // Automation — Abandonment
  abandonmentEnabled: false,
  abandonmentTimeoutMinutes: 45,
  // Automation — Follow-up
  followupEnabled: false,
  followupCount: 1,
  followupFirstInteractionDays: 2,
  followupInteractionCount: 2,
  // Message delays
  delayTextSeconds: 7,
  delayImageSeconds: 10,
  delayAudioSeconds: 15,
  delayVideoSeconds: 20,
  delayDocSeconds: 10,
  delayDefaultSeconds: 10,
  // Trigger context
  triggerContext: '',
});

const updateStateFromAssistant = assistant => {
  if (!assistant) return;
  const { config = {} } = assistant;
  Object.assign(state, {
    fixedInitialization: config.fixed_initialization ?? true,
    abandonmentEnabled: config.abandonment_enabled || false,
    abandonmentTimeoutMinutes: config.abandonment_timeout_minutes ?? 45,
    followupEnabled: config.followup_enabled || false,
    followupCount: config.followup_count ?? 1,
    followupFirstInteractionDays: config.followup_first_interaction_days ?? 2,
    followupInteractionCount: config.followup_interaction_count ?? 2,
    delayTextSeconds: config.delay_text_seconds ?? 7,
    delayImageSeconds: config.delay_image_seconds ?? 10,
    delayAudioSeconds: config.delay_audio_seconds ?? 15,
    delayVideoSeconds: config.delay_video_seconds ?? 20,
    delayDocSeconds: config.delay_doc_seconds ?? 10,
    delayDefaultSeconds: config.delay_default_seconds ?? 10,
    triggerContext: config.trigger_context || '',
  });
};

const handleSubmit = () => {
  const payload = {
    config: {
      ...props.assistant.config,
      fixed_initialization: state.fixedInitialization,
      abandonment_enabled: state.abandonmentEnabled,
      abandonment_timeout_minutes: toNumericOrNull(
        state.abandonmentTimeoutMinutes
      ),
      followup_enabled: state.followupEnabled,
      followup_count: toNumericOrNull(state.followupCount),
      followup_first_interaction_days: toNumericOrNull(
        state.followupFirstInteractionDays
      ),
      followup_interaction_count: toNumericOrNull(
        state.followupInteractionCount
      ),
      delay_text_seconds: toNumericOrNull(state.delayTextSeconds),
      delay_image_seconds: toNumericOrNull(state.delayImageSeconds),
      delay_audio_seconds: toNumericOrNull(state.delayAudioSeconds),
      delay_video_seconds: toNumericOrNull(state.delayVideoSeconds),
      delay_doc_seconds: toNumericOrNull(state.delayDocSeconds),
      delay_default_seconds: toNumericOrNull(state.delayDefaultSeconds),
      trigger_context: state.triggerContext,
    },
  };
  emit('submit', payload);
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-8">
    <!-- ═══════════ SECTION 1: Automação ═══════════ -->
    <section class="flex flex-col gap-5">
      <header class="flex flex-col gap-1">
        <h3 class="text-n-slate-12 text-base font-medium">
          {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.TITLE') }}
        </h3>
        <p class="text-n-slate-11 text-sm">
          {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.DESCRIPTION') }}
        </p>
      </header>

      <!-- Row 0: Fixed Initialization toggle -->
      <div class="flex items-center justify-between">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.FIXED_INITIALIZATION.LABEL') }}
          </label>
          <p class="text-xs text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.FIXED_INITIALIZATION.DESCRIPTION') }}
          </p>
        </div>
        <label class="toggle-switch">
          <input
            v-model="state.fixedInitialization"
            type="checkbox"
            class="absolute opacity-0 w-0 h-0 peer"
          />
          <div class="toggle-track peer-checked:!bg-[#4B7DFB] peer-focus:ring-2 peer-focus:ring-[#4B7DFB]/20" />
        </label>
      </div>

      <!-- Row 1: Abandono toggle + Tempo abandono -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.ABANDONMENT.LABEL') }}
          </label>
          <label class="toggle-switch">
            <input
              v-model="state.abandonmentEnabled"
              type="checkbox"
              class="absolute opacity-0 w-0 h-0 peer"
            />
            <div class="toggle-track peer-checked:!bg-[#4B7DFB] peer-focus:ring-2 peer-focus:ring-[#4B7DFB]/20" />
            <span class="text-sm text-n-slate-11 ml-2">
              {{ state.abandonmentEnabled
                ? t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.ACTIVE')
                : t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.INACTIVE')
              }}
            </span>
          </label>
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.ABANDONMENT.TIMEOUT_LABEL') }}
          </label>
          <Input
            v-model="state.abandonmentTimeoutMinutes"
            type="number"
            :placeholder="t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.ABANDONMENT.TIMEOUT_PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Row 2: Follow-up toggle + Quant Follow-up -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.FOLLOWUP.LABEL') }}
          </label>
          <label class="toggle-switch">
            <input
              v-model="state.followupEnabled"
              type="checkbox"
              class="absolute opacity-0 w-0 h-0 peer"
            />
            <div class="toggle-track peer-checked:!bg-[#4B7DFB] peer-focus:ring-2 peer-focus:ring-[#4B7DFB]/20" />
            <span class="text-sm text-n-slate-11 ml-2">
              {{ state.followupEnabled
                ? t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.ACTIVE')
                : t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.INACTIVE')
              }}
            </span>
          </label>
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.FOLLOWUP.COUNT_LABEL') }}
          </label>
          <Input
            v-model="state.followupCount"
            type="number"
            :placeholder="t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.FOLLOWUP.COUNT_PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Row 3: Dias 1ª interação + Quantidade interação -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.FOLLOWUP.FIRST_INTERACTION_LABEL') }}
          </label>
          <Input
            v-model="state.followupFirstInteractionDays"
            type="number"
            :placeholder="t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.FOLLOWUP.FIRST_INTERACTION_PLACEHOLDER')"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.FOLLOWUP.INTERACTION_COUNT_LABEL') }}
          </label>
          <Input
            v-model="state.followupInteractionCount"
            type="number"
            :placeholder="t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.FOLLOWUP.INTERACTION_COUNT_PLACEHOLDER')"
          />
        </div>
      </div>
    </section>

    <!-- ═══════════ SECTION 2: Tempo envio mensagens ═══════════ -->
    <section class="flex flex-col gap-5">
      <header class="flex flex-col gap-1">
        <h3 class="text-n-slate-12 text-base font-medium">
          {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.MESSAGE_DELAYS.TITLE') }}
        </h3>
        <p class="text-n-slate-11 text-sm">
          {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.MESSAGE_DELAYS.DESCRIPTION') }}
        </p>
      </header>

      <!-- Row 1: Texto, Imagem, Áudio -->
      <div class="grid grid-cols-3 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.MESSAGE_DELAYS.TEXT') }}
          </label>
          <Input
            v-model="state.delayTextSeconds"
            type="number"
            placeholder="7"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.MESSAGE_DELAYS.IMAGE') }}
          </label>
          <Input
            v-model="state.delayImageSeconds"
            type="number"
            placeholder="10"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.MESSAGE_DELAYS.AUDIO') }}
          </label>
          <Input
            v-model="state.delayAudioSeconds"
            type="number"
            placeholder="15"
          />
        </div>
      </div>

      <!-- Row 2: Vídeo, Doc, Default -->
      <div class="grid grid-cols-3 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.MESSAGE_DELAYS.VIDEO') }}
          </label>
          <Input
            v-model="state.delayVideoSeconds"
            type="number"
            placeholder="20"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.MESSAGE_DELAYS.DOC') }}
          </label>
          <Input
            v-model="state.delayDocSeconds"
            type="number"
            placeholder="10"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.MESSAGE_DELAYS.DEFAULT') }}
          </label>
          <Input
            v-model="state.delayDefaultSeconds"
            type="number"
            placeholder="10"
          />
        </div>
      </div>
    </section>

    <!-- ═══════════ SECTION 3: Contexto para Disparo ═══════════ -->
    <section class="flex flex-col gap-5">
      <header class="flex flex-col gap-1">
        <h3 class="text-n-slate-12 text-base font-medium">
          {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.TRIGGER_CONTEXT.TITLE') }}
        </h3>
        <p class="text-n-slate-11 text-sm">
          {{ t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.TRIGGER_CONTEXT.DESCRIPTION') }}
        </p>
      </header>

      <textarea
        v-model="state.triggerContext"
        :placeholder="t('CAPTAIN.ASSISTANTS.AUTOMATION_SETTINGS.TRIGGER_CONTEXT.PLACEHOLDER')"
        rows="4"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand resize-y"
      />
    </section>

    <!-- ═══════════ Submit Button ═══════════ -->
    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<style scoped>
.toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.toggle-track {
  position: relative;
  width: 2.75rem;
  height: 1.5rem;
  border-radius: 9999px;
  background-color: var(--n-alpha-3, #64748b40);
  transition: background-color 0.2s ease;
  flex-shrink: 0;
}

.toggle-track::after {
  content: '';
  position: absolute;
  top: 0.125rem;
  left: 0.125rem;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  background-color: white;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.peer:checked ~ .toggle-track::after {
  transform: translateX(1.25rem);
}
</style>
